import { EmbedAppContentKind } from "client/Api/Apps/EditApp/saveApp";
import { Dialog } from "client/Components";
import { useOpenDialog } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement } from "react";
import styles from "./EmbedAppPreviewDialog.module.less";
import classNames from "classnames";
import { EmbedAppContent } from "client/Features/Embed/EmbedAppContent";

export type EmbedAppPreviewDialogProps = {
	contentKind: EmbedAppContentKind;
	html: string;
	onClose: () => void;
};

export const EmbedAppPreviewDialog = (props: EmbedAppPreviewDialogProps): ReactElement => {
	const {
		contentKind,
		html,
		onClose
	} = props;

	return (
		<Dialog
			trackingId="embed-app-preview-dialog"
			size="large"
			isEnlargeable
		>
			<Dialog.Header
				title={localize("_PreviewWebContentApp")}
				onClose={onClose}
				hasBorder
			/>

			<Dialog.Body goesEdgeToEdge>
				<div className={styles.body}>
					<div className={styles.previews}>
						<Preview
							size={8}
							contentKind={contentKind}
							html={html}
						/>

						<Preview
							size={4}
							contentKind={contentKind}
							html={html}
						/>
					</div>
				</div>
			</Dialog.Body>

			<Dialog.Footer hasBorder />
		</Dialog>
	);
};
EmbedAppPreviewDialog.displayName = "EmbedAppPreviewDialog";

type PreviewProps = {
	size: 4 | 8;
	contentKind: EmbedAppContentKind;
	html: string;
};

const Preview = (props: PreviewProps): ReactElement => {
	const {
		size,
		contentKind,
		html
	} = props;

	const title = `${size}x${size}`;

	return (
		<div className={styles.preview}>
			<p className={styles.previewTitle} title={title}>
				{title}
			</p>

			<div
				className={classNames(
					styles.previewContent,
					previewSizeToStyleMap[size]
				)}
			>
				<EmbedAppContent
					kind={contentKind}
					html={html}
					trackingId="html-preview"
				/>
			</div>
		</div>
	);
};
Preview.displayName = "Preview";

const previewSizeToStyleMap: Record<PreviewProps["size"], string> = {
	"4": styles.previewContent_4x4,
	"8": styles.previewContent_8x8,
};

export type EmbedAppPreviewDialogInitialData = Omit<
	EmbedAppPreviewDialogProps,
	"onClose"
>;

export function useEmbedAppPreviewDialog(): [
	element: ReactElement | null,
	open: (props: EmbedAppPreviewDialogInitialData) => Promise<void>
] {
	return useOpenDialog(
		(props, onClose) => (
			<EmbedAppPreviewDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
